<template>
  <table class="table table-bordered table-striped">
    <thead>
      <tr>
        <th v-for="(col, index) in columns" :key="index">
          <span v-html="$i18n.t(col.header || col)"></span>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row of viewPoints" :key="row.id" :class="{ 'parent-row': isParentViewPoint(row) }">
        <td
          v-for="(col, colIndex) of columns"
          :key="colIndex"
          :class="{ disabled: entity(row, col) && entity(row, col).disabled }"
        >
          <template v-if="!colIndex">{{ $i18n.t(row.label) }}</template>
          <template v-else>
            <div v-for="(entity, entityIndex) of [entity(row, col)]" :key="entityIndex">
              <div
                v-for="element of entity.elements"
                :key="element.id"
                class="form-group"
                :class="{ required: element.required }"
              >
                <label class="control-label"></label>
                <input
                  v-if="element.type === elementTypes.TEXT"
                  type="text"
                  v-model="element.value"
                  class="form-control"
                />
                <input
                  v-if="element.type === elementTypes.NUMBER"
                  type="number"
                  v-model="element.value"
                  class="form-control"
                />
                <input
                  v-if="element.type === elementTypes.DATE"
                  type="date"
                  v-model="element.value"
                  class="form-control"
                />
                <select v-else-if="element.type === elementTypes.COMBO" v-model="element.value" class="form-control">
                  <option value=""></option>
                  <option
                    v-for="[id, label] in element.typeOptions.list"
                    :value="id"
                    :key="id"
                    :selected="element.value == id"
                  >
                    {{ $i18n.t(label) }}
                  </option>
                </select>
                <textarea
                  v-else-if="element.type === elementTypes.COMMENT"
                  v-model="element.value"
                  @mouseover="$event.target.classList.add('expanded')"
                  @mouseleave="$event.target.classList.remove('expanded')"
                  class="form-control comment"
                ></textarea>
                <div v-else-if="element.type === elementTypes.FINAL">
                  <input type="checkbox" v-model="element.value" />
                  <span>{{ element.typeOptions.user }}</span>
                  <span>{{ element.typeOptions.date }}</span>
                </div>
              </div>
            </div>
          </template>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import console from 'console'
import { isEmpty } from 'lodash'

/**
 * @class Checklist
 *
 * @description This component renders a checklist interface
 */
export default {
  name: 'Checklist',
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      columns: ['LABEL'],
      viewPoints: [],
      entities: [],
    }
  },
  computed: {
    elementTypes() {
      return {
        TEXT: 'text',
        DATE: 'date',
        COMBO: 'combo',
        COMMENT: 'comment',
        NUMBER: 'number',
        FINAL: 'final',
      }
    },
  },
  methods: {
    isParentViewPoint(viewPoint) {
      return !viewPoint.parent
    },
    childrenViewPoints(viewPoint) {
      return this.viewPoints.filter((vp) => vp.parent === viewPoint.id)
    },
    entity(row, col) {
      return this.entities.find((entity) => JSON.stringify(entity.id) === JSON.stringify([row.id, col.id]))
    },
    convertValue(newValue) {
      if (!isEmpty(newValue)) {
        this.columns = ['LABEL']
        this.viewPoints = []
        this.entities = []
        for (let column of newValue.ENTITIES) {
          this.columns.push({
            id: column.ID_CHECK_TYPE_COLUMN,
            header: column.ENTITY_NAME,
            disabled: !!column.READONLY,
          })
        }

        for (let viewPoint of newValue.RULES.CHECK_VIEWPOINT.sort((a, b) => a.SEQ - b.SEQ)) {
          this.viewPoints.push({
            id: viewPoint.ID_CHECK_VIEWPOINT,
            parent: viewPoint.ID_CHECK_VIEWPOINT_PARENT,
            label: viewPoint.LABEL,
            inputs: viewPoint.ID_CHECK_VIEWPOINT_VALUE.reduce((acc, cur) => {
              let inputDef = newValue.RULES.ID_CHECK_VIEWPOINT_VALUE[cur]
              let input = {
                id: cur,
                type: inputDef.TYPE,
                required: !!inputDef.REQUIRED,
                typeOptions: {},
              }

              switch (inputDef.TYPE) {
                case this.elementTypes.COMBO:
                  input.typeOptions.list = Object.entries(inputDef.ID_CHECK_RESULT)
                  break
              }

              acc.push(input)
              return acc
            }, []),
          })
        }
        if (newValue.ENTITIES.find((column) => column.hasOwnProperty('FINALIZED_USER'))) {
          this.viewPoints.push({
            id: -1,
            parent: null,
            label: this.$i18n.t('Final'),
            inputs: [
              {
                type: this.elementTypes.FINAL,
                required: false,
                typeOptions: {},
              },
            ],
          })
        }
      }

      for (let viewPoint of this.viewPoints) {
        for (let column of this.columns) {
          if (!column.id) continue
          let entity = {
            id: [viewPoint.id, column.id],
            disabled: column.disabled,
            elements: viewPoint.inputs.reduce((acc, curr) => {
              let {
                id,
                type,
                required,
                typeOptions: { ...typeOptions },
              } = curr
              let element = { id, type, required, typeOptions }

              switch (type) {
                case this.elementTypes.FINAL: {
                  let user = newValue.ENTITIES.find(
                    (entity) => entity.ID_CHECK_TYPE_COLUMN === column.id,
                  ).FINALIZED_USER
                  let date = newValue.ENTITIES.find(
                    (entity) => entity.ID_CHECK_TYPE_COLUMN === column.id,
                  ).FINALIZED_DATE
                  element.typeOptions.user = user
                  element.typeOptions.date = date
                  element.value = !!date
                  break
                }
                default:
                  try {
                    element.value = newValue.ENTITIES.find(
                      (entity) => entity.ID_CHECK_TYPE_COLUMN === column.id,
                    ).ID_CHECK_VIEWPOINT[viewPoint.id].ID_CHECK_VIEWPOINT_VALUE[curr.id].DATA_RECORD
                  } catch (e) {
                    // @todo log
                    console.error(e)
                  }
              }

              acc.push(element)
              return acc
            }, []),
          }
          this.entities.push(entity)
        }
      }
    },
  },
  watch: {
    entities: {
      handler(newValue) {
        newValue.forEach((entity) => {
          const [viewPointID, columnID] = entity.id

          entity.elements.forEach((element) => {
            let ENTITY = this.value.ENTITIES.find((ENTITY) => ENTITY.ID_CHECK_TYPE_COLUMN === columnID)

            switch (element.type) {
              case this.elementTypes.FINAL:
                if (!ENTITY.READONLY) {
                  ENTITY.FINALIZED_DATE = element.value ? Date.now() : null
                  ENTITY.FINALIZED_USER = element.value ? this.$CORE.externalParams.user : null
                }
                break
              default:
                ENTITY.ID_CHECK_VIEWPOINT[viewPointID].ID_CHECK_VIEWPOINT_VALUE[element.id].DATA_RECORD = element.value
            }
          })
        })
      },
      deep: true,
    },
    value: {
      handler(newValue) {
        this.convertValue(newValue)
      },
    },
  },
  created() {
    this.convertValue(this.value)
  },
}
</script>

<style lang="scss" scoped>
tr.parent-row,
tr.parent-row td {
  background-color: #d8cbf1 !important;
}
textarea.comment {
  &:not(.expanded) {
    height: 30px !important;
    min-height: auto !important;
  }
}
</style>
